import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { HiMagnifyingGlass, HiXMark, HiClock } from 'react-icons/hi2';
import Fuse from 'fuse.js';
import { getSearchableContent } from '../utils/searchData';
import { useHotkeys } from 'react-hotkeys-hook';
import SectionTitle from '../components/SectionTitle';
import { sanitizeInput, rateLimit, logSecurityEvent } from '../utils/security';

const Search = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { searchHistory, addSearchHistory } = useApp();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  // Initialize Fuse.js for fuzzy search
  const searchContent = getSearchableContent(t);
  const fuse = new Fuse(searchContent, {
    keys: ['title', 'content', 'keywords'],
    threshold: 0.3,
    includeScore: true,
    includeMatches: true
  });

  // Perform search
  const performSearch = useCallback((searchQuery) => {
    // Sanitize input
    const sanitized = sanitizeInput(searchQuery);
    
    if (!sanitized.trim()) {
      setResults([]);
      setIsSearching(false);
      return;
    }
    
    // Rate limit searches (max 20 per minute)
    if (!rateLimit('search', 20, 60000)) {
      logSecurityEvent('search_rate_limit_exceeded');
      return;
    }
    
    // Limit query length
    if (sanitized.length > 200) {
      logSecurityEvent('search_query_too_long', { length: sanitized.length });
      return;
    }

    setIsSearching(true);
    const searchResults = fuse.search(sanitized);
    setResults(searchResults);
    setIsSearching(false);

    // Add to history
    if (sanitized.trim()) {
      addSearchHistory(sanitized.trim());
    }
  }, [fuse, addSearchHistory]);

  // Debounced search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      performSearch(query);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [query, performSearch]);

  // Keyboard shortcut to focus search
  useHotkeys('ctrl+k, cmd+k', (e) => {
    e.preventDefault();
    document.getElementById('search-input')?.focus();
  });

  const handleResultClick = (result) => {
    navigate(result.item.path);
  };

  const handleHistoryClick = (historyQuery) => {
    setQuery(historyQuery);
    performSearch(historyQuery);
  };

  const clearQuery = () => {
    setQuery('');
    setResults([]);
  };

  return (
    <div className="max-w-3xl mx-auto">
      <div className="mb-8">
        <SectionTitle level={1} icon={HiMagnifyingGlass}>
          {t('search.title')}
        </SectionTitle>
        <p className="text-gray-600 dark:text-gray-400 text-sm">
          {t('search.shortcut')}
        </p>
      </div>

      {/* Search input */}
      <div className="relative mb-8">
        <div className="absolute inset-y-0 start-0 ps-4 flex items-center pointer-events-none">
          <HiMagnifyingGlass className="h-6 w-6 text-gray-400 dark:text-gray-500" />
        </div>
        <input
          id="search-input"
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t('search.placeholder')}
          className="w-full ps-12 pe-12 py-4 text-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white border-2 border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-teal-500 dark:focus:ring-teal-400 focus:border-transparent placeholder:text-gray-400 dark:placeholder:text-gray-500 transition-colors"
          autoFocus
        />
        {query && (
          <button
            onClick={clearQuery}
            className="absolute inset-y-0 end-0 pe-4 flex items-center hover:scale-110 transition-transform"
          >
            <HiXMark className="h-6 w-6 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300" />
          </button>
        )}
      </div>

      {/* Search results */}
      {query ? (
        <div>
          {isSearching ? (
            <div className="text-center py-12">
              <div className="animate-spin inline-block w-8 h-8 border-4 border-teal-500 dark:border-teal-400 border-t-transparent rounded-full" />
              <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">{t('search.searching')}</p>
            </div>
          ) : results.length > 0 ? (
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 font-medium">
                {t('search.resultsCount', { count: results.length })}
              </p>
              <div className="space-y-3">
                {results.map((result) => (
                  <button
                    key={result.item.id}
                    onClick={() => handleResultClick(result)}
                    className="w-full text-start p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-teal-500 dark:hover:border-teal-500 hover:shadow-md transition-all group"
                  >
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-1 group-hover:text-teal-600 dark:group-hover:text-teal-400 transition-colors">
                      {result.item.title}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
                      {result.item.content}
                    </p>
                    <p className="text-xs text-teal-600 dark:text-teal-400 mt-2 font-medium">
                      {result.item.path}
                    </p>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="text-center py-12">
              <HiMagnifyingGlass className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
              <p className="text-gray-500 dark:text-gray-400">{t('search.noResults')}</p>
              <p className="text-sm text-gray-400 dark:text-gray-500 mt-2">{t('search.tryDifferent')}</p>
            </div>
          )}
        </div>
      ) : (
        // Recent searches
        searchHistory.length > 0 && (
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <HiClock className="h-5 w-5 text-gray-400 dark:text-gray-500" />
              {t('search.recentSearches')}
            </h2>
            <div className="space-y-2">
              {searchHistory.map((item, index) => (
                <button
                  key={index}
                  onClick={() => handleHistoryClick(item.query)}
                  className="w-full text-start flex items-center gap-3 p-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-teal-500 dark:hover:border-teal-500 hover:shadow-sm transition-all group"
                >
                  <HiClock className="h-5 w-5 text-gray-400 dark:text-gray-500 group-hover:text-teal-500 dark:group-hover:text-teal-400 flex-shrink-0 transition-colors" />
                  <span className="text-gray-700 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-white flex-1 transition-colors">{item.query}</span>
                  <span className="text-xs text-gray-400 dark:text-gray-500 flex-shrink-0">
                    {new Date(item.timestamp).toLocaleDateString()}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )
      )}
    </div>
  );
};

export default Search;
